import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { replace } from 'redux-first-history';
import { LinearProgress, Paper, Stack, Typography } from '@rossum/ui/material';
import {
  contactSupportLink,
  statusPageURL,
  termsAndConditionsURL,
} from '../../constants/values';
import { link } from '../../lib/formaterValues';
import ElisHead from './components/ElisHead';
import ErrorDetails, { ErrorDetailsState } from './components/ErrorDetails';

type Props = { refresh: () => void };
type MessageKey = 'maintenance' | 'throttleError';
type MaintenaceRouterState = {
  initialCountdown?: number;
  messageKey: MessageKey;
  debug?: ErrorDetailsState;
};

const Maintenance = ({ refresh }: Props) => {
  const location = useLocation<MaintenaceRouterState>();

  const {
    initialCountdown = 10,
    messageKey = 'maintenance',
    debug,
  } = location.state ?? {};

  const [countdown, setCountdown] = useState(initialCountdown);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const intl = useIntl();

  useEffect(() => {
    const interval = setInterval(() => {
      if (!detailsExpanded) {
        setCountdown(n => Math.max(n - 1, 0));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [detailsExpanded]);

  useEffect(() => {
    if (countdown <= 0) refresh();
  }, [countdown, refresh]);

  const progressPercentage =
    (100 / initialCountdown) * (initialCountdown - countdown);

  return (
    <Stack
      spacing={0}
      sx={{
        width: '100%',
        height: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        overflow: 'auto',
      }}
      component={Paper}
      elevation={1}
    >
      <Stack
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          flexShrink: 0,
        }}
        data-page-title="maintenance"
      >
        <ElisHead />
        <Typography
          variant="h5"
          sx={{ letterSpacing: '3.26px', textTransform: 'uppercase' }}
        >
          {intl.formatMessage({ id: 'containers.maintenance.title' })}
        </Typography>
        <Typography variant="body2">
          {intl.formatMessage(
            { id: `containers.${messageKey}.text` },
            {
              policyLink: link(termsAndConditionsURL),
            }
          )}
        </Typography>
        <LinearProgress
          sx={{ width: '200px', height: '5px' }}
          variant="determinate"
          value={progressPercentage}
        />
        <Typography variant="caption" color="text.secondary">
          {countdown}
          {intl.formatMessage({ id: 'containers.maintenance.smallText' })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mt: '105px' }}
        >
          {intl.formatMessage(
            { id: 'containers.maintenance.badge.text1' },
            {
              contactLink: link(contactSupportLink, 'contactLink'),
              statusLink: link(statusPageURL, 'statusLink'),
            }
          )}
        </Typography>
      </Stack>
      <ErrorDetails debug={debug} onExpandChange={setDetailsExpanded} />
    </Stack>
  );
};

export default connect<unknown, { refresh: () => void }>(null, {
  refresh: () => replace('/'),
})(Maintenance);
